import { ActivityLaneModel, ActivityStage } from '../@types/service-activity.type';
import { ServiceActivityResponseDataType } from '../@types/service.type';
import {
  ACTIVITY_LANES,
  ACTIVITY_STATUS_COLUMNS,
  ActivityLane,
} from '../constants/service-activity.constant';

/** Coerce a payload count to a usable number. The BFF types these as `number`,
 *  but a null/undefined slipping through must render 0, never NaN. */
const toCount = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

/**
 * Magnitude of `value` against `max`, on a square-root curve.
 *
 * Linear scaling is unusable here: a column can hold 254 next to 1, and every
 * small stage collapses to an invisible sliver. `sqrt` compresses the top end
 * so small-but-nonzero stages stay legible while the big one still dominates.
 *
 * This is only defensible because the exact count is always rendered next to
 * the bar — the bar is a shape cue for scanning, not the data itself.
 *
 * Any non-zero value returns at least `MIN_VISIBLE` so "some" never renders
 * identically to "none".
 */
const MIN_VISIBLE = 0.04;

export const magnitudeRatio = (value: number, max: number): number => {
  const count = toCount(value);
  // `max` is guarded for finiteness too — `NaN <= 0` is false, so a bare
  // `max <= 0` check lets NaN through and yields a NaN width.
  const ceiling = toCount(max);
  if (count <= 0 || ceiling <= 0) return 0;
  if (count >= ceiling) return 1;
  return Math.max(MIN_VISIBLE, Math.sqrt(count / ceiling));
};

/**
 * Aggregate the activity payload into the three flow-rail lanes.
 *
 * Always returns all three lanes with every stage present, so the rail keeps
 * its shape on empty data instead of structurally collapsing.
 */
export const buildFlowRail = (
  rows: readonly ServiceActivityResponseDataType[] | undefined
): ActivityLaneModel[] => {
  const source = rows ?? [];

  // Column-wise totals across every row.
  const counts = new Map<string, number>();
  for (const column of ACTIVITY_STATUS_COLUMNS) {
    let sum = 0;
    for (const row of source) sum += toCount(row?.[column.field]);
    counts.set(column.field, sum);
  }

  // Largest stage within each lane — the magnitude denominator.
  const laneMax = new Map<ActivityLane, number>();
  for (const column of ACTIVITY_STATUS_COLUMNS) {
    const current = laneMax.get(column.lane) ?? 0;
    laneMax.set(column.lane, Math.max(current, counts.get(column.field) ?? 0));
  }

  return ACTIVITY_LANES.map((meta) => {
    // Exception scales against the job-flow lane; see ActivityLaneMeta.
    const denominator = laneMax.get(meta.scaleAgainst) ?? 0;

    const stages: ActivityStage[] = ACTIVITY_STATUS_COLUMNS.filter(
      (column) => column.lane === meta.lane
    ).map((column) => {
      const count = counts.get(column.field) ?? 0;
      return {
        field: column.field,
        header: column.header,
        group: column.group,
        count,
        magnitude: magnitudeRatio(count, denominator),
        alert: Boolean(column.alert),
      };
    });

    return {
      lane: meta.lane,
      label: meta.label,
      stages,
      total: stages.reduce((sum, stage) => sum + stage.count, 0),
    };
  });
};
